// components/Sections/Admin/Matriculas/Configuracion.jsx
//
// Una configuración de matrícula por recinto: valor por tipo de cliente, vigencia
// y si se vende.
//
// Oct-2026: cuatro valores (no vecino, vecino, adulto mayor, funcionario) y
// vigencias de 1, 2 o 3 años o de por vida. En el formulario la vigencia es un solo
// select ('anual-2', 'indefinida'); al backend va como tipoVigencia + aniosVigencia.
//
// Cambios respecto a la versión anterior:
//  · Se cargan todas las configuraciones una vez y se filtra en el cliente. Antes había
//    filtros arriba (servidor) y en las columnas (cliente) que se contradecían y daban
//    una tabla vacía con "Crear primera configuración"; y la carga inicial iba doble.
//  · "Nueva configuración" ya no ofrece recintos que tienen una: el backend busca por
//    recinto y la sobrescribía en silencio con "configurada correctamente".
//  · El buscador de recintos del modal filtraba contra JSX y nunca encontraba nada.
//  · La carga es por fila: antes activar o desactivar una hacía girar todas.
import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, InputNumber, Select, Table, Modal, Alert, Popconfirm } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  FiPlus, FiEdit2, FiPower, FiLayers, FiShoppingBag, FiPauseCircle, FiInfo,
} from 'react-icons/fi';
import MatriculaService from '../../../../service/Matriculas';
import RecintoService from '../../../../service/Recintos';
import { useNotify } from '../../../../providers/NotificationProvider';
import { formatearNombre } from '../../../../utils/texto';
import {
  EncabezadoAdmin, TarjetaResumen, FiltroChips, ChipEstado, ChipNeutro, claseBtnPrimario, claseBtnSecundario,
} from '../../../Ui/Admin/PiezasAdmin';

const formatearMoneda = (monto) => new Intl.NumberFormat('es-CL', {
  style: 'currency', currency: 'CLP', minimumFractionDigits: 0,
}).format(monto || 0);

const mensaje = (e, porDefecto) => e?.error || e?.message || porDefecto;

const OPCIONES_VIGENCIA = [
  { value: 'anual-1', label: '1 año desde la compra' },
  { value: 'anual-2', label: '2 años desde la compra' },
  { value: 'anual-3', label: '3 años desde la compra' },
  { value: 'indefinida', label: 'De por vida' },
];

const TRAMOS = [
  { key: 'particular', label: 'No vecino', requerido: true },
  { key: 'vecino', label: 'Vecino' },
  { key: 'adultoMayor', label: 'Adulto mayor' },
  { key: 'funcionario', label: 'Funcionario' },
];

const vigenciaDe = (c) => (c.tipoVigencia === 'anual' ? `anual-${c.aniosVigencia || 1}` : 'indefinida');
const textoVigencia = (c) => {
  if (c.tipoVigencia !== 'anual') return 'De por vida';
  const n = c.aniosVigencia || 1;
  return n === 1 ? '1 año' : `${n} años`;
};
// Las configuraciones viejas solo tienen valorMatricula (= no vecino).
const valoresDe = (c) => ({
  particular: c.valores?.particular ?? c.valorMatricula ?? 0,
  vecino: c.valores?.vecino ?? null,
  adultoMayor: c.valores?.adultoMayor ?? null,
  funcionario: c.valores?.funcionario ?? null,
});
const aPayload = ({ vigencia, valores, ...resto }) => {
  const [tipoVigencia, anios] = (vigencia || 'indefinida').split('-');
  const limpio = Object.fromEntries(TRAMOS.map(t => [t.key, valores?.[t.key] ?? null]));
  return {
    ...resto,
    valores: limpio,
    valorMatricula: limpio.particular,
    tipoVigencia,
    aniosVigencia: tipoVigencia === 'anual' ? Number(anios) : undefined,
  };
};

const InputPesos = (props) => (
  <InputNumber className="w-full" min={0} step={1000}
    formatter={(value) => (value === null || value === undefined || value === '' ? '' : `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))}
    parser={(value) => value.replace(/\$\s?|(\.*)/g, '')} {...props} />
);

const ConfiguracionMatriculas = () => {
  const [form] = Form.useForm();
  const notify = useNotify();

  const [recintos, setRecintos] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [vista, setVista] = useState('todas'); // todas | activas | inactivas
  const [vigencia, setVigencia] = useState('todas'); // todas | anual | indefinida

  const [modal, setModal] = useState({ abierto: false, editando: null });
  const [guardando, setGuardando] = useState(false);
  const [accionando, setAccionando] = useState(null); // _id de la fila en curso

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [resRecintos, resConfigs] = await Promise.all([
        RecintoService.List({ limit: 1000 }),
        MatriculaService.ListarMatriculas({}),
      ]);
      setRecintos((resRecintos.data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
      setConfigs(resConfigs.data || []);
    } catch (e) {
      setError(mensaje(e, 'No se pudieron cargar las configuraciones'));
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const recintosConConfig = useMemo(() => new Set(configs.map(c => c.recinto?._id).filter(Boolean)), [configs]);
  const recintosDisponibles = recintos.filter(r => !recintosConConfig.has(r._id));

  const conteo = {
    total: configs.length,
    activas: configs.filter(c => c.activa).length,
    inactivas: configs.filter(c => !c.activa).length,
  };
  const filtradas = configs.filter(c =>
    (vista === 'todas' || (vista === 'activas' ? c.activa : !c.activa))
    && (vigencia === 'todas' || c.tipoVigencia === vigencia));

  const abrirNueva = () => {
    form.resetFields();
    form.setFieldsValue({ vigencia: 'anual-1', valores: { particular: 0 } });
    setModal({ abierto: true, editando: null });
  };
  const abrirEditar = (c) => {
    form.setFieldsValue({
      recintoId: c.recinto?._id,
      valores: valoresDe(c),
      vigencia: vigenciaDe(c),
      descripcion: c.descripcion,
    });
    setModal({ abierto: true, editando: c });
  };
  const cerrarModal = () => { setModal({ abierto: false, editando: null }); form.resetFields(); };

  // Editar NO manda `activa`: así guardar no reactiva una configuración inactiva
  // (el backend depende de eso).
  const guardar = async (values) => {
    setGuardando(true);
    try {
      // `modo: 'crear'` hace que el backend responda 409 en vez de sobrescribir
      // si otro admin configuró el recinto mientras tanto.
      await MatriculaService.ConfigurarMatricula({ ...aPayload(values), modo: modal.editando ? 'editar' : 'crear' });
      notify.success(modal.editando ? 'Configuración actualizada' : 'Configuración creada');
      cerrarModal();
      cargar();
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo guardar la configuración'));
    } finally {
      setGuardando(false);
    }
  };

  // Desactivar va por recinto, no por _id de la configuración.
  const desactivar = async (c) => {
    setAccionando(c._id);
    try {
      await MatriculaService.DesactivarMatricula(c.recinto._id);
      notify.success('Matrícula desactivada: ya no se puede comprar');
      cargar();
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo desactivar'));
    } finally {
      setAccionando(null);
    }
  };

  // Activar reenvía todos los campos: el backend exige el valor no vecino y, si no
  // le llegan los demás tramos, los dejaría vacíos.
  const activar = async (c) => {
    setAccionando(c._id);
    try {
      await MatriculaService.ConfigurarMatricula({
        ...aPayload({ recintoId: c.recinto._id, valores: valoresDe(c), vigencia: vigenciaDe(c), descripcion: c.descripcion }),
        activa: true,
      });
      notify.success('Matrícula activada: vuelve a estar a la venta');
      cargar();
    } catch (e) {
      notify.error(mensaje(e, 'No se pudo activar'));
    } finally {
      setAccionando(null);
    }
  };

  const columns = [
    {
      title: 'Recinto',
      key: 'recinto',
      sorter: (a, b) => (a.recinto?.nombre || '').localeCompare(b.recinto?.nombre || '', 'es'),
      render: (_, c) => (
        <div className="min-w-0">
          {/* Si el recinto se borró, el populate deja `recinto` en null: antes eso tumbaba la tabla. */}
          <div className="font-bold text-gray-900 truncate">{c.recinto ? formatearNombre(c.recinto.nombre) : 'Recinto eliminado'}</div>
          <div className="text-xs text-gray-500 truncate">{c.descripcion || c.recinto?.direccion || '—'}</div>
        </div>
      ),
    },
    {
      title: 'Valores',
      key: 'valor',
      width: 230,
      sorter: (a, b) => a.valorMatricula - b.valorMatricula,
      render: (_, c) => {
        const v = valoresDe(c);
        return (
          <div className="text-xs leading-5 tabular-nums">
            {TRAMOS.map(t => (
              <div key={t.key} className="flex justify-between gap-3">
                <span className="text-gray-500">{t.label}</span>
                <span className={t.requerido ? 'font-extrabold text-gray-900' : 'font-bold text-gray-700'}>
                  {v[t.key] === null ? '—' : v[t.key] > 0 ? formatearMoneda(v[t.key]) : 'Gratis'}
                </span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Vigencia',
      key: 'vigencia',
      width: 140,
      render: (_, c) => <ChipNeutro>{textoVigencia(c)}</ChipNeutro>,
    },
    {
      title: 'Venta',
      dataIndex: 'activa',
      key: 'activa',
      width: 150,
      render: (a) => <ChipEstado activo={a} labelActivo="En venta" labelInactivo="No se vende" />,
    },
    {
      title: <span className="sr-only">Acciones</span>,
      key: 'acciones',
      width: 220,
      align: 'right',
      render: (_, c) => (
        <span className="inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <button type="button" onClick={() => abrirEditar(c)} disabled={!c.recinto}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-40">
            <FiEdit2 size={12} /> Editar
          </button>
          {c.recinto && (c.activa ? (
            <Popconfirm title="¿Dejar de vender esta matrícula?"
              description="Deja de ofrecerse en la web. Quienes ya la tienen la mantienen."
              okText="Sí, desactivar" cancelText="No" okButtonProps={{ danger: true }}
              onConfirm={() => desactivar(c)}>
              <button type="button" disabled={accionando === c._id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-gray-500 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-60">
                {accionando === c._id ? <LoadingOutlined /> : <FiPower size={12} />} Desactivar
              </button>
            </Popconfirm>
          ) : (
            <Popconfirm title="¿Volver a vender esta matrícula?" description="Se ofrecerá de nuevo en la web." okText="Sí, activar" cancelText="No"
              onConfirm={() => activar(c)}>
              <button type="button" disabled={accionando === c._id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-[#00266b] text-xs font-bold hover:brightness-105 transition-all disabled:opacity-60">
                {accionando === c._id ? <LoadingOutlined /> : <FiPower size={12} />} Activar
              </button>
            </Popconfirm>
          ))}
        </span>
      ),
    },
  ];

  const editando = modal.editando;

  return (
    <div className="max-w-[1280px]">
      <EncabezadoAdmin titulo="Configuración de" destacado="matrículas"
        subtitulo="Qué cuesta la matrícula de cada recinto, cuánto dura y si está a la venta">
        <button type="button" onClick={abrirNueva} disabled={cargando || recintosDisponibles.length === 0} className={claseBtnPrimario}
          title={recintosDisponibles.length === 0 ? 'Todos los recintos ya tienen configuración' : undefined}>
          <FiPlus size={16} /> Nueva configuración
        </button>
      </EncabezadoAdmin>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <TarjetaResumen icon={<FiLayers size={19} />} label="Configuradas" value={conteo.total}
          activa={vista === 'todas'} onClick={() => setVista('todas')} />
        <TarjetaResumen icon={<FiShoppingBag size={19} />} label="En venta" value={conteo.activas}
          activa={vista === 'activas'} onClick={() => setVista('activas')} />
        <TarjetaResumen icon={<FiPauseCircle size={19} />} label="No se venden" value={conteo.inactivas}
          activa={vista === 'inactivas'} onClick={() => setVista('inactivas')} />
      </div>

      <div className="flex flex-wrap items-center gap-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
        <FiltroChips label="Vigencia" valor={vigencia} onChange={setVigencia} opciones={[
          { value: 'todas', label: 'Todas' }, { value: 'anual', label: 'Por años' }, { value: 'indefinida', label: 'De por vida' },
        ]} />
        <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
          <FiInfo size={13} />
          {recintosDisponibles.length
            ? `${recintosDisponibles.length} recinto${recintosDisponibles.length !== 1 ? 's' : ''} sin configuración`
            : 'Todos los recintos tienen configuración'}
        </span>
      </div>

      {error && <Alert type="error" showIcon className="mb-4" message={error}
        action={<button type="button" onClick={cargar} className={claseBtnSecundario}>Reintentar</button>} />}

      <div className="admin-table-card">
        <Table
          columns={columns}
          dataSource={filtradas}
          loading={cargando}
          rowKey="_id"
          scroll={{ x: 760 }}
          pagination={filtradas.length > 20 ? { pageSize: 20, style: { padding: '12px 16px' } } : false}
          rowClassName={() => 'fila-clicable'}
          onRow={(c) => ({ onClick: () => c.recinto && abrirEditar(c) })}
          locale={{
            emptyText: (
              <div className="py-10 text-center">
                <p className="font-bold text-gray-900 m-0">{configs.length ? 'Ninguna configuración coincide' : 'Aún no hay configuraciones'}</p>
                <p className="text-sm text-gray-500 m-0 mt-1">
                  {configs.length ? 'Prueba con otro filtro.' : 'Crea la primera para empezar a vender matrículas.'}
                </p>
              </div>
            ),
          }}
        />
      </div>

      {/* Cómo funciona: antes era una tarjeta azul de 5 viñetas arriba de la tabla. */}
      <details className="mt-5 rounded-2xl bg-white border border-gray-100 p-4 group">
        <summary className="cursor-pointer text-sm font-bold text-gray-700 list-none flex items-center gap-2">
          <FiInfo size={15} className="text-primary" /> Cómo funcionan las matrículas
        </summary>
        <ul className="mt-3 mb-0 pl-5 space-y-1.5 text-sm text-gray-600">
          <li>Cada recinto tiene su propia configuración.</li>
          <li><strong>Valores:</strong> se cobra el más bajo al que la persona inscrita tiene derecho. Vecino y adulto mayor se validan con la Tarjeta Vecino (adulto mayor = 60+ y vecino); funcionario, con la nómina de funcionarios. Un valor vacío no se ofrece.</li>
          <li><strong>Vigencia:</strong> 1, 2 o 3 años desde la compra, o de por vida.</li>
          <li><strong>Caducidad:</strong> si la persona no compra nada en el recinto durante 2 años, la matrícula vence aunque sea de por vida, y tiene que pagarla de nuevo.</li>
          <li>Cuando un producto exige matrícula y la persona no tiene una vigente, se suma sola a la compra (web y caja). Si ya la tiene, la venta sigue normal.</li>
          <li>Solo las configuraciones en venta se cobran; desactivar no afecta a quienes ya la tienen.</li>
        </ul>
      </details>

      <Modal
        open={modal.abierto}
        onCancel={cerrarModal}
        width={560}
        title={editando ? <>Editar matrícula de <span className="hl-amarillo">{formatearNombre(editando.recinto?.nombre)}</span></> : 'Nueva configuración'}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cerrarModal} className={claseBtnSecundario}>Cancelar</button>
            <button type="button" onClick={() => form.submit()} disabled={guardando} className={claseBtnPrimario}>
              {guardando && <LoadingOutlined />} {editando ? 'Guardar cambios' : 'Crear configuración'}
            </button>
          </div>
        }
      >
        {editando && !editando.activa && (
          <Alert type="warning" showIcon className="mb-4"
            message="Esta configuración no se está vendiendo"
            description="Guardar los cambios no la reactiva: para volver a venderla usa «Activar» en la tabla." />
        )}
        {editando && (
          <p className="text-xs text-gray-500 mb-4">Los cambios de valor o vigencia aplican a las compras nuevas; las matrículas ya compradas no cambian.</p>
        )}

        <Form form={form} layout="vertical" onFinish={guardar} autoComplete="off" className="form-compacto">
          {editando ? (
            <Form.Item name="recintoId" hidden><Input /></Form.Item>
          ) : (
            <Form.Item name="recintoId" label="Recinto" rules={[{ required: true, message: 'Selecciona un recinto' }]}
              extra={recintosConConfig.size > 0 && 'Los recintos que ya tienen configuración se editan desde la tabla.'}>
              <Select placeholder="Selecciona un recinto" showSearch optionFilterProp="label"
                options={recintosDisponibles.map(r => ({ value: r._id, label: formatearNombre(r.nombre) }))} />
            </Form.Item>
          )}

          <p className="text-xs font-bold text-gray-700 mb-2">Valor por tipo de cliente</p>
          <div className="grid grid-cols-2 gap-x-4">
            {TRAMOS.map(t => (
              <Form.Item key={t.key} name={['valores', t.key]} label={t.label}
                rules={t.requerido
                  ? [{ required: true, message: 'Ingresa el valor' }, { type: 'number', min: 0, message: 'Debe ser 0 o más' }]
                  : [{ type: 'number', min: 0, message: 'Debe ser 0 o más' }]}
                extra={t.requerido ? '0 = gratis' : 'Vacío = paga no vecino'}>
                <InputPesos placeholder={t.requerido ? '12000' : '—'} />
              </Form.Item>
            ))}
          </div>
          <Form.Item name="vigencia" label="Vigencia" rules={[{ required: true, message: 'Selecciona la vigencia' }]}
            extra="Sea cual sea la vigencia, vence si pasan 2 años sin compras en el recinto.">
            <Select options={OPCIONES_VIGENCIA} />
          </Form.Item>

          <Form.Item name="descripcion" label="Descripción (opcional)" className="!mb-0">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount
              placeholder="Ej: Matrícula anual para acceso a los talleres del recinto" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConfiguracionMatriculas;
